'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { usePathname } from 'next/navigation';
import type { RouteMetaPayload } from '../../_libs/site-shell/routeMeta';

function isSameRouteMeta(previous: RouteMetaPayload | null, next: RouteMetaPayload | null): boolean {
  if (previous === next) return true;
  if (!previous || !next) return false;

  return (
    previous.metaProps === next.metaProps &&
    previous.pageTargeting === next.pageTargeting &&
    previous.disableRaptiveFooter === next.disableRaptiveFooter &&
    previous.article === next.article &&
    previous.post === next.post &&
    previous.term === next.term &&
    previous.disablePageTracking === next.disablePageTracking
  );
}

interface RouteMetaStore {
  clear: () => void;
  getSnapshot: () => RouteMetaPayload | null;
  register: (payload: RouteMetaPayload | null, registrationPathname?: string) => void;
  subscribe: (listener: () => void) => () => void;
}

function createRouteMetaStore(): RouteMetaStore {
  let routeMeta: RouteMetaPayload | null = null;
  const listeners = new Set<() => void>();

  const notify = () => {
    listeners.forEach(listener => listener());
  };

  return {
    clear: () => {
      if (routeMeta === null) return;
      routeMeta = null;
      notify();
    },
    getSnapshot: () => routeMeta,
    register: (payload, _registrationPathname) => {
      if (!isSameRouteMeta(routeMeta, payload)) {
        routeMeta = payload;
        notify();
      }
    },
    subscribe: listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

interface RouteMetaRegistrationContextValue {
  pathname: string;
  registerRouteMeta: (payload: RouteMetaPayload | null, registrationPathname?: string) => void;
}

const RouteMetaStoreContext = createContext<RouteMetaStore | null>(null);
const RouteMetaRegistrationContext = createContext<RouteMetaRegistrationContextValue | null>(null);

export function RouteMetaProvider({ children }: { children: React.ReactNode }) {
  const store = useRef(createRouteMetaStore()).current;
  const pathname = usePathname();
  const [prevPathname, setPrevPathname] = useState(pathname);

  if (prevPathname !== pathname) {
    setPrevPathname(pathname);
    store.clear();
  }

  const registerRouteMeta = useCallback(
    (payload: RouteMetaPayload | null, registrationPathname?: string) => {
      if (payload === null && registrationPathname !== undefined && registrationPathname !== pathname) {
        return;
      }
      store.register(payload);
    },
    [pathname, store],
  );

  const registrationValue = useMemo(
    () => ({ pathname, registerRouteMeta }),
    [pathname, registerRouteMeta],
  );

  return (
    <RouteMetaStoreContext.Provider value={store}>
      <RouteMetaRegistrationContext.Provider value={registrationValue}>{children}</RouteMetaRegistrationContext.Provider>
    </RouteMetaStoreContext.Provider>
  );
}

export function useRouteMeta(): RouteMetaPayload | null {
  const store = useContext(RouteMetaStoreContext);
  if (!store) {
    throw new Error('useRouteMeta must be used within RouteMetaProvider');
  }

  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

export function useRouteMetaRegistration(): RouteMetaRegistrationContextValue {
  const context = useContext(RouteMetaRegistrationContext);
  if (!context) {
    throw new Error('useRouteMetaRegistration must be used within RouteMetaProvider');
  }
  return context;
}
